import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import saveAs from "file-saver";
import { firstValueFrom } from "rxjs";
import { InvalidOperationException } from "src/app/shared/exceptions/exceptions";
import { HttpResponseHeader } from "src/app/shared/models/http-header";
import { BackendHttpParameterService } from "src/app/shared/services/backend-http-parameter.service";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ExternalSystemCommunicationStateService } from "src/app/shared/services/external-system-communication-state.service";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { NotificationService } from "src/app/shared/services/notification/notification.service";
import { environment } from "src/environments/environment";

import { TermLifeInsuranceStaticDocumentType } from "./shared/term-life-insurance-document-type";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { QuoteHttpFacadeService } from "./quote-http-facade.service";
import { TermLifeInsuranceConstantRequestDto } from "./request/term-life-insurance-request.dto";
import { ProductResponseDto } from "./response/product-response.dto";
import {
  TermLifeInsuranceProductConfigurationResponseDto,
  TermLifeInsuranceQuoteResponseDto,
} from "./response/term-life-insurance-quote-response.dto";

/**
 * This http service should only be called via our store. This way we can guarantee the the following concerns are being handled:
 * - Integration with the {@link ExternalSystemCommunicationStateService} (e.g to display the spinner when there is a pending http request)
 * - Integration with the {@link NotificationService} to publish notifications (e.g when an exception occurred)
 *
 * In case we don't want to go via our store, we should use the {@link QuoteHttpFacadeService} which then handles those responsibilities.
 */
@Injectable({
  providedIn: "root",
})
export class QuoteHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly _backendHttpParameterService: BackendHttpParameterService,
  ) {}

  getTermLifeInsuranceConstantQuote(quoteId: string) {
    const baseUrl = environment.backendBaseUrl;

    const httpHeaders =
      this._backendHttpParameterService.createDefaultBackendHttpHeaders();

    return this.httpClient.get<TermLifeInsuranceQuoteResponseDto>(
      baseUrl + `/quotes/termlifeinsurance/constant/${quoteId}`,
      { headers: httpHeaders },
    );
  }

  createTermLifeInsuranceConstantQuote(
    requestDto: TermLifeInsuranceConstantRequestDto,
  ) {
    const baseUrl = environment.backendBaseUrl;

    const httpHeaders =
      this._backendHttpParameterService.createDefaultBackendHttpHeaders();

    return this.httpClient.post<TermLifeInsuranceQuoteResponseDto>(
      baseUrl + "/quotes/termlifeinsurance/constant",
      requestDto,
      { headers: httpHeaders },
    );
  }

  updateTermLifeInsuranceConstantQuote(
    quoteId: string,
    requestDto: TermLifeInsuranceConstantRequestDto,
  ) {
    const baseUrl = environment.backendBaseUrl;

    const httpHeaders =
      this._backendHttpParameterService.createDefaultBackendHttpHeaders();

    return this.httpClient.patch<TermLifeInsuranceQuoteResponseDto>(
      baseUrl + `/quotes/termlifeinsurance/constant/${quoteId}`,
      requestDto,
      { headers: httpHeaders },
    );
  }

  getTermLifeInsuranceProductConfiguration() {
    const baseUrl = environment.backendBaseUrl;

    const httpHeaders =
      this._backendHttpParameterService.createDefaultBackendHttpHeaders();

    return this.httpClient.get<TermLifeInsuranceProductConfigurationResponseDto>(
      baseUrl + `/quotes/termlifeinsurance/constant/productconfiguration`,
      { headers: httpHeaders },
    );
  }

  getProduct(quoteId: string) {
    const baseUrl = environment.backendBaseUrl;

    const httpHeaders =
      this._backendHttpParameterService.createDefaultBackendHttpHeaders();

    return this.httpClient.get<ProductResponseDto>(
      baseUrl + `/quotes/${quoteId}/product`,
      { headers: httpHeaders },
    );
  }

  async downloadTermLifeInsuranceDocument(
    termLifeInsuranceStaticDocumentType: TermLifeInsuranceStaticDocumentType,
  ) {
    const httpHeaders =
      this._backendHttpParameterService.createDefaultBackendHttpHeaders();

    const termLifeInsuranceDocumentUrl =
      this.createDownloadTermLifeInsuranceDocumentUrl(
        termLifeInsuranceStaticDocumentType,
      );

    const httpResponse = await firstValueFrom(
      this.httpClient.get(termLifeInsuranceDocumentUrl.toString(), {
        observe: "response",
        responseType: "blob",
        headers: httpHeaders,
      }),
    );

    if (httpResponse.body === null) {
      throw new InvalidOperationException(
        "It should not be possible that the body of the response is empty when downloading a document",
      );
    }

    const fileNameWithExtension = this.getFileNameWithExtension(httpResponse);

    saveAs(httpResponse.body, fileNameWithExtension);
  }

  createDownloadTermLifeInsuranceDocumentUrl(
    termLifeInsuranceStaticDocumentType: TermLifeInsuranceStaticDocumentType,
  ) {
    const baseUrl = environment.backendBaseUrl;

    let termLifeInsuranceDocumentUrl = new URL(
      baseUrl +
        `/quotes/termlifeinsurance/documents/${termLifeInsuranceStaticDocumentType}`,
    );

    termLifeInsuranceDocumentUrl =
      this._backendHttpParameterService.addLanguageQueryString(
        termLifeInsuranceDocumentUrl,
      );

    return termLifeInsuranceDocumentUrl;
  }

  private getFileNameWithExtension(response: HttpResponse<Blob>) {
    const contentDispositionHeaderValue = response.headers.get(
      HttpResponseHeader.CONTENT_DISPOSITION,
    );

    if (contentDispositionHeaderValue === null) {
      throw new InvalidOperationException(
        `The backend did not provide a '${HttpResponseHeader.CONTENT_DISPOSITION}' header in the response`,
      );
    }

    const fileNameWithExtension =
      fileNameWithExtensionFromDispositionHeaderRegex.exec(
        contentDispositionHeaderValue,
      );

    if (fileNameWithExtension === null) {
      throw new InvalidOperationException(
        `The regex pattern '${String(fileNameWithExtensionFromDispositionHeaderRegex)}' was not able to match anything against the '${HttpResponseHeader.CONTENT_DISPOSITION}' header value of '${contentDispositionHeaderValue}'`,
      );
    }

    return fileNameWithExtension[0];
  }
}

// https://regex101.com/r/JC3Bws/2
// eslint-disable-next-line no-useless-escape
const fileNameWithExtensionFromDispositionHeaderRegex = /(?<=\").*(?=\")/;
